import { useEffect, useRef, useState, type RefObject } from 'react';

/**
 * Largeur utile (contentBox) d'un élément, observée en continu. Alimente le
 * calcul de disposition justifiée — la grille se recompose au redimensionnement
 * (responsive, XPHOTO-28) sans recharger les données.
 *
 * ⚠ PERF (audit B1) : la valeur rendue est un ENTIER, et les rafales de
 * redimensionnement sont coalescées sur une frame. `entry.contentRect.width` est
 * FRACTIONNAIRE : chaque frame d'un glissement de fenêtre poussait une largeur inédite,
 * ce qui invalidait le `useMemo` de `computeJustifiedLayout` — lequel reparcourt TOUS les
 * assets chargés. À 30 000 vignettes, c'était un O(n) complet à 60 Hz pendant tout le
 * glissement. `Math.round` élimine l'écrasante majorité de ces invalidations (une
 * variation sous le pixel n'a aucun effet visible sur la disposition) ; la coalescence
 * `requestAnimationFrame` supprime le reste (au plus un recalcul par frame peinte).
 *
 * Repli si `ResizeObserver` est absent (certains environnements de test) : on
 * lit une fois la largeur au montage.
 */
export function useElementWidth(ref: RefObject<HTMLElement | null>): number {
  const [width, setWidth] = useState(0);
  const frameRef = useRef<number | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    if (typeof ResizeObserver === 'undefined') {
      setWidth(Math.round(el.clientWidth));
      return;
    }

    /** Pose la largeur au plus une fois par frame (dernière mesure gagne). */
    const schedule = (next: number): void => {
      const rounded = Math.round(next);
      if (typeof requestAnimationFrame === 'undefined') {
        setWidth(rounded);
        return;
      }
      if (frameRef.current !== null) cancelAnimationFrame(frameRef.current);
      frameRef.current = requestAnimationFrame(() => {
        frameRef.current = null;
        // `setWidth` avec une valeur identique est un no-op côté React : aucune
        // recomposition n'est déclenchée si l'entier n'a pas bougé.
        setWidth(rounded);
      });
    };

    const observer = new ResizeObserver((entries) => {
      const last = entries[entries.length - 1];
      if (last) schedule(last.contentRect.width);
    });
    observer.observe(el);
    setWidth(Math.round(el.clientWidth));
    return () => {
      observer.disconnect();
      if (frameRef.current !== null && typeof cancelAnimationFrame !== 'undefined') {
        cancelAnimationFrame(frameRef.current);
        frameRef.current = null;
      }
    };
  }, [ref]);

  return width;
}
